import React, {useState, useEffect, useRef} from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter} from 'react-router-dom';
import { connect } from 'react-redux';

import {SET_LOADING_SPINNER_ENABLE, SET_LOADING_OVERLAY_ENABLE} from 'actions/types';
import DatabaseService from 'services/DatabaseService.js'
import {Button, Tab} from 'semantic-ui-react';
import {colors, fontSizes, fonts, spacings, shadow, buttons} from 'style';
import {setSidebarEnable} from 'actions/sidebar.js';
import {List} from 'components/list'
import {DeleteModal} from 'components/modals'
import InvitationUploadForm from './InvitationUploadForm'

import {getInvitations} from 'reducers/invitations';
import {fetchInvitations} from 'actions/invitations';
import {Buffer} from "buffer"

const formatAssignedList = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v === 'string' || typeof v === 'number') return String(v)
            if (typeof v !== 'object') return ''
            return (
                v.name ??
                v.label ??
                v.title ??
                v.code ??
                v.key ??
                v.id ??
                ''
            )
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    if (parts.length) return parts.join(', ')
    if (Array.isArray(value)) return `${value.length}`
    return ''
}

const formatInvitationEmployeeAttributes = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            const name =
                v?.employeeAttribute?.label ??
                v?.employeeAttribute?.name ??
                v?.employeeAttributeId ??
                v?.id ??
                ''
            const val = v?.data
            if (!name) return ''
            if (val === undefined || val === null || val === '') return ''
            return `${name}: ${val}`
        })
        .filter((s) => s && String(s).trim().length)

    return parts.join('\n')
}

const formatInvitationSegments = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            return (
                v?.team?.name ??
                v?.team?.lc_name ??
                v?.teamId ??
                v?.id ??
                ''
            )
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    return parts.join('\n')
}

const formatInvitationRewards = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            const lines = []
            const stars = Number(v?.rewardPoints ?? 0)
            const gems = Number(v?.rewardResources ?? 0)
            if (Number.isFinite(stars) && stars > 0) lines.push(`Stars: ${stars}`)
            if (Number.isFinite(gems) && gems > 0) lines.push(`Gems: ${gems}`)
            return lines.join('\n')
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    // If there are multiple rewards (rare), keep them separated
    return parts.join('\n')
}

const getInvitationEmployeeAttributes = (invitation) =>
    invitation?.invitationEmployeeAttributes ??
    invitation?.employeeAttributes ??
    invitation?.employee_attributes

const getInvitationSegments = (invitation) =>
    invitation?.invitationSegments ??
    invitation?.segments

const getInvitationRewards = (invitation) =>
    invitation?.invitationRewards ??
    invitation?.rewards

const styles = {
    content: {
        fontSize: fontSizes.body,
        lineHeight: '1.5',
        minHeight: '100%',
        padding: spacings.base,
        backgroundColor: colors.themeGrey,
    },
    topPanel: {
        display: 'flex',
        marginBottom: 16
    }
}

const invitationFields = [
    {
        name: 'Code',
        key: 'code',
        default: true,
        searchable: true
    },
    {
        name: 'Uses',
        key: 'uses',
        default: true
    },
    {
        name: 'Employee Attributes',
        key: 'invitationEmployeeAttributes',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationEmployeeAttributes(getInvitationEmployeeAttributes(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Segments',
        key: 'invitationSegments',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationSegments(getInvitationSegments(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Rewards',
        key: 'invitationRewards',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationRewards(getInvitationRewards(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Created At',
        key: 'createdAt',
        default: true,
        defaultSort: true,
        defaultSortOrder: 'descending',
        type: 'date'
    },
    {
        name: 'Link',
        key: 'link'
    },
    {
        name: 'Single Use',
        key: 'singleUse'
    }
]

const InvitationsContainer = ({t, dispatch, fetchInvitations}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [update, setUpdate] = useState()
    const [invitationModal, setInvitationModal] = useState()
    const [editingInvitation, setEditingInvitation] = useState(null)
    const [deletingInvitation, setDeletingInvitation] = useState(null)
    const [error, setError] = useState()

    useEffect(() => {
        dispatch(setSidebarEnable(true))
    }, [])

    const withInvitationEditFields = (params) => ({
        ...params,
        invitationEmployeeAttributes: true,
        invitationSegments: true,
        invitationRewards: true,
        invitationData: true
    })

    const fetchPublicInvitations = (params) => {
        return fetchInvitations(withInvitationEditFields({
            ...params,
            public: true
        }))
    }

    const fetchSingleUserInvitations = (params) => {
        return fetchInvitations(withInvitationEditFields({
            ...params,
            singleUse: true
        }))
    }

    const fetchAllInvitations = (params) => {
        return fetchInvitations(withInvitationEditFields(params))
    }

    const uploadInvitation = async ({ invitation, reward, overwrite } = {}) => {
        if (!invitation) return false

        dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:true});
        try {
            // Rewards first so InvitationReward tags exist before invitations reference them.
            if (reward) {
                const rewardResult = await DatabaseService.postInvitationRewards({
                    invitationRewards: {csv: {base64: Buffer.from(reward).toString('base64')}}
                })
                .catch((error)=> {
                    console.error(error)
                    setError(error)
                    return null
                })
                if (!rewardResult) return false
            }

            const result = await DatabaseService.postInvitations({
                invitations: {csv: {base64: Buffer.from(invitation).toString('base64')}},
                overwrite: overwrite === true
            })
            .catch((error)=> {
                console.error(error)
                setError(error)
                return null
            })
            if (result) {
                setError()
                setUpdate(!update)
                return true
            }
            return false
        } finally {
            dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:false});
        }
    }

    const openInvitationModal = () => {
        setEditingInvitation(null)
        setInvitationModal(true)
    }

    const openEditInvitation = (invitation) => {
        setError()
        setEditingInvitation(invitation)
        setInvitationModal(true)
    }

    const openDeleteInvitation = (invitation) => {
        if (!invitation?.id) return
        setDeletingInvitation(invitation)
    }

    const cancelDeleteInvitation = () => {
        setDeletingInvitation(null)
    }

    const confirmDeleteInvitation = async () => {
        const invitation = deletingInvitation
        setDeletingInvitation(null)
        if (!invitation?.id) return

        dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:true});
        try {
            await DatabaseService.deleteInvitation({ invitationId: invitation.id })
            setError()
            setUpdate(!update)
        } catch (error) {
            console.error(error)
            window.alert(error?.message || String(error))
        } finally {
            dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:false});
        }
    }

    const cancelModal = () => {
        setInvitationModal(false)
        setEditingInvitation(null)
        setError()
    }

    const invitationRowActions = [
        {
            name: 'pencil',
            invoke: openEditInvitation
        },
        {
            name: 'trash',
            invoke: openDeleteInvitation,
            color: colors.warningRed
        }
    ]

    const panes = [
        {
          menuItem: t('Public'),
          pane: {
            content: (
                <Tab.Pane>
                    {activeIndex === 0 && (
                        <List
                            listKey="invitations-public"
                            getData={fetchPublicInvitations}
                            searchData={fetchPublicInvitations}
                            update={update}
                            type='table'
                            actions={invitationRowActions}
                            fields={invitationFields}
                        />
                    )}
                </Tab.Pane>
            )
          }
        },
        {
          menuItem: t('Single Use'),
          pane: {
            content: (
                <Tab.Pane>
                    {activeIndex === 1 && (
                        <List
                            listKey="invitations-single-use"
                            getData={fetchSingleUserInvitations}
                            searchData={fetchSingleUserInvitations}
                            update={update}
                            type='table'
                            actions={invitationRowActions}
                            fields={invitationFields}
                        />
                    )}
                </Tab.Pane>
            )
          }
        },
        {
            menuItem: t('All Invitations'),
            pane: {
                content: (
                    <Tab.Pane>
                        {activeIndex === 2 && (
                            <List
                                listKey="invitations-all"
                                getData={fetchAllInvitations}
                                searchData={fetchAllInvitations}
                                update={update}
                                type='table'
                                actions={invitationRowActions}
                                fields={invitationFields}
                            />
                        )}
                    </Tab.Pane>
                )
            }
        }
    ]

    return (
        <div style={styles.content}>
            <div style={styles.topPanel}>
                <div>
                    <h2>{t('Add')}</h2>
                    <Button onClick={openInvitationModal}>{t('Add Invitation')}</Button>
                </div>
            </div>
            <Tab
                panes={panes}
                renderActiveOnly={false}
                activeIndex={activeIndex}
                onTabChange={(_e, data) => setActiveIndex(Number(data.activeIndex) || 0)}
            />
            <InvitationUploadForm
                open={invitationModal}
                close={cancelModal}
                upload={uploadInvitation}
                error={error}
                editing={editingInvitation}
            />
            <DeleteModal
                open={Boolean(deletingInvitation)}
                cancel={cancelDeleteInvitation}
                confirm={confirmDeleteInvitation}
                tableName={t('Invitation')}
            />
        </div>
    )
}

const mapStateToProps = (state, ownProps) => {
    return {invitations: getInvitations(state.invitations)};
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchInvitations: (params, force) => {
            return dispatch(fetchInvitations(params, force))
        },
        dispatch: dispatch
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation('invitations')(InvitationsContainer)));